import PhotoSizeSelectActualOutlinedIcon from '@mui/icons-material/PhotoSizeSelectActualOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip.tsx';
import { ErrorContainer } from '@openx/components-core/lib/CreativeMediaError/ErrorContainer.tsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_IFRAME_HEIGHT, DEFAULT_IFRAME_WIDTH } from './constants.ts';
import { PreviewModal } from './PreviewModal.tsx';
import { useCreativeSandbox } from './useCreativeSandbox.ts';

type CreativeThumbnailProps = {
  html: string | null | undefined;
  width?: number | null;
  height?: number | null;
  onDimensionsDetected?: (dimensions: { width: number; height: number }) => void;
};

export const CreativeThumbnail = ({ html, width, height, onDimensionsDetected }: CreativeThumbnailProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { iframeRef, iframeSrc, isLoading, isError, onLoad } = useCreativeSandbox({ html, onDimensionsDetected });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const hasKnownDimensions = width != null && height != null && width > 0 && height > 0;
  const iframeWidth = hasKnownDimensions ? Number(width) : DEFAULT_IFRAME_WIDTH;
  const iframeHeight = hasKnownDimensions ? Number(height) : DEFAULT_IFRAME_HEIGHT;

  useEffect(() => {
    const el = containerRef.current;

    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const isContainerReady = containerSize.width > 0;
  const fitScale = isContainerReady
    ? Math.min(containerSize.width / iframeWidth, containerSize.height / iframeHeight, 1)
    : 1;

  useEffect(() => {
    if (isLoading || !isContainerReady || !html || !hasKnownDimensions) {
      setIsRevealed(false);
      return;
    }
    // Wait one frame so the scale transform is applied before revealing
    const raf = requestAnimationFrame(() => setIsRevealed(true));
    return () => cancelAnimationFrame(raf);
  }, [isLoading, isContainerReady, html, hasKnownDimensions]);

  const showPlaceholder = !html;
  const showSpinner = !!html && !isRevealed && !isError;
  const showError = !!html && isError && !isLoading;
  const showIframe = isRevealed && !isError;

  const handleOpen = useCallback(() => setIsFullscreen(true), []);
  const handleClose = useCallback(() => setIsFullscreen(false), []);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleOpen();
      }
    },
    [handleOpen]
  );

  return (
    <>
      <Box
        sx={{
          borderLeft: 1,
          borderColor: 'divider',
          p: 4,
        }}
      >
        <Typography variant="h3" sx={{ mb: 1.5 }} data-test="preview-section-title">
          {t('Creative Preview')}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.boxLight',
              borderRadius: 1,
              width: '100%',
              minWidth: 160,
              aspectRatio: '16 / 9',
              overflow: 'hidden',
              ...(html && {
                cursor: 'pointer',
              }),
            }}
            ref={containerRef}
            onClick={html && !isFullscreen ? handleOpen : undefined}
            {...(html &&
              !isFullscreen && {
                role: 'button',
                tabIndex: 0,
                'aria-label': t('Open fullscreen preview'),
                onKeyDown: handleKeyDown,
              })}
            data-test="creative-preview-container"
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
              }}
            >
              {showSpinner && <CircularProgress size={20} />}
              {showPlaceholder && (
                <AlertTooltip
                  title={
                    <Typography variant="caption">
                      {t("Preview will appear once 'Ad Markup Payload' is filled in.")}
                    </Typography>
                  }
                  icon={<PhotoSizeSelectActualOutlinedIcon fontSize="medium" />}
                  color={theme.palette.text.secondary}
                />
              )}
            </Box>

            {showError && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 3,
                }}
              >
                <ErrorContainer
                  height={Math.round(containerSize.height * 0.8)}
                  width={Math.round(containerSize.width * 0.8)}
                />
              </Box>
            )}

            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: Math.round(iframeWidth * fitScale),
                height: Math.round(iframeHeight * fitScale),
                overflow: 'hidden',
                pointerEvents: 'none',
                visibility: showIframe ? 'visible' : 'hidden',
              }}
            >
              <iframe
                ref={iframeRef}
                src={iframeSrc}
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
                width={iframeWidth}
                height={iframeHeight}
                style={{
                  border: 0,
                  transformOrigin: '0 0',
                  transform: `scale(${fitScale})`,
                }}
                title={t('Creative preview')}
                onLoad={onLoad}
                data-test="creative-preview-iframe"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {html && (
        <PreviewModal
          isOpen={isFullscreen}
          onClose={handleClose}
          markup={html}
          isError={isError}
          width={iframeWidth}
          height={iframeHeight}
        />
      )}
    </>
  );
};
